import { type ReactNode } from 'react';
import {
  APIComponentAttributes,
  ArdoqId,
  ViewpointDetailEntry,
} from '@ardoq/api-types';
import { type ComponentSurveyWithOptionalApprovalStatus } from './SurveyButtons';
import { EnhancedScopeData } from '@ardoq/data-model';
import { type OpenEditPriorityFieldsProps, WidgetId } from '../types';
import { capitalize } from 'lodash';
import {
  DetailsDrawer,
  type DetailsDrawerLabels,
} from '../drawers/DetailsDrawer';
import {
  ReferencesDrawer,
  type ReferencesDrawerLabels,
} from '../drawers/ReferencesDrawer';
import { PermissionContext } from '@ardoq/access-control';

export type SidebarContentLabels = {
  detailsDrawer?: DetailsDrawerLabels;
  referencesDrawer?: ReferencesDrawerLabels;
};

type GetSidebarContentArgs = {
  widgetId: WidgetId | null;
  scopeData?: EnhancedScopeData;
  component?: APIComponentAttributes;
  detailEntries?: ViewpointDetailEntry[];
  surveys?: ComponentSurveyWithOptionalApprovalStatus[] | null;
  onComponentClick?: (componentId: ArdoqId) => void;
  trackEvent?: (name: string, metadata?: Record<string, unknown>) => void;
  onSurveyClick?: (surveyId: ArdoqId) => void;
  permissionContext: PermissionContext;
  priorityFieldNames?: string[];
  noPriorityFieldsBanner?: ReactNode;
  openEditPriorityFieldsProps?: OpenEditPriorityFieldsProps;
  labels?: SidebarContentLabels;
};

export const SidebarContent = ({
  widgetId,
  scopeData,
  component,
  detailEntries,
  surveys,
  onSurveyClick,
  onComponentClick,
  trackEvent,
  permissionContext,
  priorityFieldNames,
  noPriorityFieldsBanner,
  openEditPriorityFieldsProps,
  labels,
}: GetSidebarContentArgs) => {
  if (widgetId === 'details') {
    return (
      <DetailsDrawer
        scopeData={scopeData}
        component={component}
        detailEntries={detailEntries}
        surveys={surveys}
        onSurveyClick={onSurveyClick}
        permissionContext={permissionContext}
        priorityFieldNames={priorityFieldNames}
        noPriorityFieldsBanner={noPriorityFieldsBanner}
        openEditPriorityFieldsProps={openEditPriorityFieldsProps}
        labels={labels?.detailsDrawer}
      />
    );
  }
  if (widgetId === 'references') {
    return (
      <ReferencesDrawer
        scopeData={scopeData}
        component={component}
        onComponentClick={onComponentClick}
        detailEntries={detailEntries}
        trackEvent={trackEvent}
        labels={labels?.referencesDrawer}
      />
    );
  }

  return null;
};

type GetDrawerTitleArgs = {
  widgetId: WidgetId | null;
  component?: APIComponentAttributes;
  detailsFallbackTitle?: string;
};

export const getDrawerTitle = ({
  widgetId,
  component,
  detailsFallbackTitle = 'Details',
}: GetDrawerTitleArgs): string => {
  if (widgetId === 'details') {
    return component?.type ?? detailsFallbackTitle;
  }
  if (widgetId === 'references') {
    return capitalize(widgetId);
  }
  return widgetId ?? '';
};
